import React, { ReactElement, useCallback, useState, useContext } from "react";
import { Icon, useRequestInit, RouteParams, hasNoRoleIsw, useFeature, Notificaties2Context } from "adviesbox-shared";
import { useFormikContext, FormikProps } from "formik";
import { IndicatieOptions } from "../../../../../.generated/forms/formstypes";
import { AcceptatieState } from "../infra/acceptatie-schema";
import { useParams } from "react-router-dom";
import { sendReportRequest } from "../../../../../rapportage2/rapportage-api";
import { RapportageState } from "../../../../../rapportage2/infra/rapportage-schema";
import { NieuwRapport } from "../../../../../.generated/foundation/foundationtypes";
import { Button, Spinner } from "react-bootstrap";
import styles from "./acceptatie-card.module.scss";

type AcceptatieCardProps = {
  formikProps?: FormikProps<AcceptatieState>;
};

export const AcceptatieCard = ({ formikProps }: AcceptatieCardProps): ReactElement => {
  const { values } = useFormikContext<AcceptatieState>();
  const { user, settings } = useRequestInit<{ vestiging: string; adviesdossier: string }>();
  const isIswMedewerker = !hasNoRoleIsw(user);
  const paramContext = useParams<RouteParams>();
  const featureRapport2Overzicht = useFeature("FeatureRapport2Overzicht");
  const [isPrinting, setIsPrinting] = useState(false);
  const { subscribeCorrelationId } = useContext(Notificaties2Context);

  const rapportageState: RapportageState = {
    structuurId: "00000000-0000-0000-0000-000000000004",
    naam: "Acceptatie",
    volgnummer: 1,
    elementen: [],
    filteredElementen: [],
    selectedElement: ""
  };

  const getRequestInit = useCallback(
    (correlationId: string): RequestInit => {
      const nieuwRapport: NieuwRapport = {
        adviesDossierId: paramContext.adviesdossier,
        voorstelId: paramContext.voorstel,
        rapportStructuurId: "00000000-0000-0000-0000-000000000004",
        gewensteRapportNaam: null,
        preview: false
      };

      const formData = new FormData();
      formData.append("JsonValues", JSON.stringify(nieuwRapport));

      return {
        headers: {
          authorization: `${user?.token_type} ${user?.access_token}`,
          "Ocp-Apim-Subscription-Key": settings.OcpApimSubscriptionKey,
          vestigingId: paramContext.vestiging,
          correlationId: correlationId
        },
        method: "POST",
        body: formData
      };
    },
    [settings, user, paramContext]
  );

  const requestInitNieuwRapport = useCallback(
    (correlationId: string): RequestInit => {
      return getRequestInit(correlationId);
    },
    [getRequestInit]
  );

  const verwerkEnSubscribe = useCallback(async (): Promise<void> => {
    setIsPrinting(true);
    const result = await subscribeCorrelationId(
      `${settings.foundationOrigin}/FrontBus/nieuw-rapport`,
      requestInitNieuwRapport,
      /* istanbul ignore next */ () => {
        setIsPrinting(false);
      }
    );
    if (!result) {
      setIsPrinting(false);
    }
  }, [setIsPrinting, settings, requestInitNieuwRapport, subscribeCorrelationId]);

  const renderButtons = (): ReactElement | null => {
    if (!featureRapport2Overzicht) return null;

    return (
      <div className={styles.cardButtonContainer}>
        <Button
          data-testid="acceptatie-button"
          className="btn btn-sm"
          onClick={async (): Promise<void> => {
            await verwerkEnSubscribe();
          }}
          disabled={isPrinting}
          style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
          title="Overzicht downloaden"
        >
          {isPrinting ? <Spinner animation="border" size="sm" /> : <Icon name="download" iconSize="l" />}
        </Button>
        {isIswMedewerker && process.env.REACT_APP_RAPPORT_CONNECTION_STRING && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
            onClick={async () => {
              if (process.env.REACT_APP_RAPPORT_CONNECTION_STRING) {
                await sendReportRequest(
                  rapportageState,
                  paramContext.adviesdossier,
                  paramContext.voorstel,
                  user,
                  paramContext.vestiging
                );
              }
            }}
          >
            Rapport queue (dev&apos;s)
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={styles.cardContainer}>
      {renderButtons()}
      <div className="row align-items-center mb-4">
        <div className="col-8 pl-4">Indicatie</div>
        <div className="col-4">
          {values.acceptatieCard.acceptatieIndicatie === IndicatieOptions.Afgekeurd ? (
            <>
              <span className="text-danger">{values.acceptatieCard.acceptatieIndicatie}*&nbsp;&nbsp;</span>
              <Icon name="kruis" />
            </>
          ) : values.acceptatieCard.acceptatieIndicatie === IndicatieOptions.Goedgekeurd ? (
            <>
              <span className="text-success">{values.acceptatieCard.acceptatieIndicatie}*&nbsp;&nbsp;</span>
              <Icon name="vink" />
            </>
          ) : (
            <>
              <span className="text-warning">{values.acceptatieCard.acceptatieIndicatie}*&nbsp;&nbsp;</span>
            </>
          )}
        </div>
      </div>
      {values.acceptatieCard.toelichtingRegels && values.acceptatieCard.toelichtingRegels?.length > 0 && (
        <>
          <strong className="mt-6 ml-2">Toelichting</strong>
          <ul>
            {values.acceptatieCard.toelichtingRegels?.map((c, k) => (
              <li key={k}>{c.regel}</li>
            ))}
          </ul>
        </>
      )}
      {values.acceptatieCard.voetnoten && values.acceptatieCard.voetnoten?.length > 0 && (
        <>
          <div className="px-2">
            {values.acceptatieCard.voetnoten?.map((c, k) => (
              /* istanbul ignore next */
              <p className="small" key={k}>
                {c.voetnoot}
              </p>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== "production") AcceptatieCard.displayName = "AcceptatieCard";
